import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axiosConfig';
import {
  Container,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Card
} from 'react-bootstrap';
import './CreateWebsite.css';

const CreateWebsite = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    brand_name: '',
    http_link: '',
    logo_path: '',
    vision: '',
    mission: '',
    about_us: '',
    email: '',
    phone_number: '',
    address: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.post('/sites', formData);

      if (response.data.success) {
        setSuccessMessage('Website created successfully! Redirecting...');
        setTimeout(() => {
          navigate('/super-admin/websites');
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to create website.');
      }
    } catch (err) {
      setError('An error occurred while creating the website.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-website-container">
      {/* Header */}
      <div className="create-website-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="create-website-title">Create New Website</h1>
            <p className="websites-subtitle">Add a new website to the system</p>
          </div>
          <Button
            variant="outline-secondary"
            className="btn-cancel"
            onClick={() => navigate('/super-admin/websites')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Websites
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" className="alert-custom alert-danger-custom">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" className="alert-custom alert-success-custom">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {/* Form */}
      <Card className="create-website-form-card">
        <Form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-info-circle"></i>
              Basic Information
            </h3>
            <div className="form-grid">
              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom required">
                  Brand Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="brand_name"
                  value={formData.brand_name}
                  onChange={handleInputChange}
                  className="form-control-custom"
                  placeholder="Enter brand name"
                  required
                />
                <Form.Text className="form-help-text">
                  The official name of your brand or company
                </Form.Text>
              </Form.Group>

              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom required">
                  Website URL
                </Form.Label>
                <Form.Control
                  type="url"
                  name="http_link"
                  value={formData.http_link}
                  onChange={handleInputChange}
                  className="form-control-custom"
                  placeholder="https://example.com"
                  required
                />
                <Form.Text className="form-help-text">
                  Full URL including http:// or https://
                </Form.Text>
              </Form.Group>

              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  Logo Path
                </Form.Label>
                <Form.Control
                  type="text"
                  name="logo_path"
                  value={formData.logo_path}
                  onChange={handleInputChange}
                  className="form-control-custom"
                  placeholder="/assets/images/logo.png"
                />
                <Form.Text className="form-help-text">
                  Path to your logo file within the project
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {/* Vision & Mission Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-bullseye"></i>
              Vision & Mission
            </h3>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                Vision Statement
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="vision"
                value={formData.vision}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="What is the long-term vision for your organization?"
              />
              <div className="char-counter">
                {formData.vision.length}/500 characters
              </div>
            </Form.Group>

            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                Mission Statement
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="mission"
                value={formData.mission}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="What is the purpose and mission of your organization?"
              />
              <div className="char-counter">
                {formData.mission.length}/500 characters
              </div>
            </Form.Group>
          </div>

          {/* About Us Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-building"></i>
              About Us
            </h3>
            <Form.Group className="form-group-custom">
              <Form.Label className="form-label-custom">
                About Us Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="about_us"
                value={formData.about_us}
                onChange={handleInputChange}
                className="form-control-custom"
                placeholder="Tell us about your company, history, values, and what makes you unique..."
              />
              <div className="char-counter">
                {formData.about_us.length}/1000 characters
              </div>
            </Form.Group>
          </div>

          {/* Contact Information Section */}
          <div className="form-section">
            <h3 className="form-section-title">
              <i className="fas fa-address-book"></i>
              Contact Information
            </h3>
            <div className="form-grid">
              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  Email Address
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-control-custom"
                  placeholder="contact@example.com"
                />
                <Form.Text className="form-help-text">
                  Primary contact email address
                </Form.Text>
              </Form.Group>

              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  Phone Number
                </Form.Label>
                <Form.Control
                  type="text"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  className="form-control-custom"
                  placeholder="+1 (555) 123-4567"
                />
                <Form.Text className="form-help-text">
                  Primary contact phone number
                </Form.Text>
              </Form.Group>

              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  Address
                </Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-control-custom"
                  placeholder="Enter full business address"
                />
                <Form.Text className="form-help-text">
                  Physical address of your business
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-buttons">
            <Button
              type="submit"
              className="btn-create"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Creating Website...
                </>
              ) : (
                <>
                  <i className="fas fa-plus-circle me-2"></i>
                  Create Website
                </>
              )}
            </Button>
            
            <Button
              variant="outline-secondary"
              className="btn-cancel"
              onClick={() => navigate('/super-admin/websites')}
              disabled={loading}
            >
              <i className="fas fa-times me-2"></i>
              Cancel
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CreateWebsite;